import React from "react";
import StripeConnect from "../stripeConnect/stripeConnect";
import FreedomPay from "../freedomPay/freedomPay";

/**
 * Props forwarded to the underlying StripeConnect component.
 * Only include fields that callers actually use — all are optional
 * so each context supplies only what it needs.
 *
 * Note: StripeConnect does NOT support an `onPaymentSuccess` callback.
 * Stripe uses `redirect: "always"`, so payment completion is handled
 * by the `/confirm` route (ConfirmPayment polls `/checkDeposit`).
 * For in-page callbacks, see FreedomPayComponentProps.onPaymentSuccess.
 */
interface StripeProps {
  bookingId?: string;
  pendingBookingId?: string;
  bookingData?: React.ComponentProps<typeof StripeConnect>["bookingData"];
  showInfo?: boolean;
  mode?: React.ComponentProps<typeof StripeConnect>["mode"];
  action?: string;
  pendingPreorderId?: string;
  amount?: number;
  name?: string;
  email?: string;
  customerNotes?: string;
  dietaryRequirements?: React.ComponentProps<typeof StripeConnect>["dietaryRequirements"];
  vouchers?: Array<{ code: string; amountToRedeem: number }>;
  /** Refuse credit-funded cards, for booking types with credit card payments disabled. */
  disableCreditCards?: boolean;
}

/**
 * Props forwarded to the underlying FreedomPay component.
 */
interface FreedomPayComponentProps {
  bookingId?: string | false;
  bookingData?: React.ComponentProps<typeof FreedomPay>["bookingData"];
  onFinish?: React.ComponentProps<typeof FreedomPay>["onFinish"];
  onPaymentSuccess?: React.ComponentProps<typeof FreedomPay>["onPaymentSuccess"];
  onPaymentUnconfirmed?: React.ComponentProps<typeof FreedomPay>["onPaymentUnconfirmed"];
  mode?: React.ComponentProps<typeof FreedomPay>["mode"];
  showInfo?: boolean;
  action?: string;
  pendingBookingId?: string | false;
  pendingPreorderId?: string | false;
  amount?: number;
  name?: string;
  email?: string;
  customerNotes?: string;
  vouchers?: Array<{ code: string; amountToRedeem: number }>;
}

interface PaymentProcessorSwitchProps {
  /** Whether to render StripeConnect (true) or FreedomPay (false). */
  useStripeConnect: boolean;
  /** Props forwarded when rendering StripeConnect. */
  stripeProps: StripeProps;
  /** Props forwarded when rendering FreedomPay. */
  freedomPayProps: FreedomPayComponentProps;
}

/**
 * Encapsulates the Stripe-vs-FreedomPay branching logic used in
 * secureYourBooking, confirmClientDetails and PaymentStage.
 *
 * Callers pass `useStripeConnect` to choose the processor and supply
 * the relevant props for each branch.
 */
const PaymentProcessorSwitch: React.FC<PaymentProcessorSwitchProps> = ({
  useStripeConnect,
  stripeProps,
  freedomPayProps,
}) => {
  if (useStripeConnect) {
    return <StripeConnect {...stripeProps} />;
  }

  return <FreedomPay {...freedomPayProps} />;
};

export default PaymentProcessorSwitch;
